import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { hasRichTextContent } from '@/lib/render/hasRichTextContent';
import { RichText } from '@/lib/render/RichText';
import { Text } from '@/lib/render/Text';

interface PromoBadgeProps {
  prefix: string;
  price: string;
  unit: string;
  /**
   * CMS rich text (SH-424). When it carries content it replaces the three
   * tiers entirely: a paragraph renders at the prefix size, and **bold** text
   * inside it at the price size — as does a heading, kept working for the
   * badges authored before SH-449 (see `.promo-badge-rich` in
   * `app/globals.css`). Bold is the mode to use: a heading element holding
   * nothing but a price puts that price into the page outline for no SEO gain.
   * This is the badge's only mode that needs no price placeholder, so it is
   * what makes the badge usable on pages without a location.
   */
  richText?: BlocksContent;
  className?: string;
  variant?: 'default' | 'offer';
}

const sizeVariants = {
  default: {
    wrapper: 'w-[7em] md:w-[9em] lg:w-[13em]',
    prefix: 'text-[0.6em] md:text-[0.9em] lg:text-[1.3em] font-medium',
    price: 'text-[1.2em] md:text-[1.7em] lg:text-[2.4em] font-bold',
    unit: 'text-[0.6em] md:text-[0.9em] lg:text-[1.2em] font-medium',
  },
  offer: {
    wrapper: 'w-[6em] md:w-[6em] lg:w-[10em] leading-tight',
    prefix: 'text-[1.2em] md:text-[0.6em] lg:text-[1.6em] font-medium',
    price: 'text-[1.2em] md:text-[1.2em] lg:text-[1.6em] font-medium',
    unit: 'text-[1.2em] md:text-[0.6em] lg:text-[1.6em] font-medium',
  },
} as const;

export function PromoBadge({
  prefix,
  price,
  unit,
  richText,
  className = '',
  variant = 'default',
}: PromoBadgeProps) {
  const sizes = sizeVariants[variant];
  // An "empty" richtext field is a paragraph holding one empty text node, which
  // is truthy — so the fall-back to the tiers has to ask `hasRichTextContent`.
  const rich = hasRichTextContent(richText) ? richText : undefined;

  return (
    <div
      className={classNames(
        'flex aspect-square',
        sizes.wrapper,
        'flex-col items-center justify-center rounded-full bg-gradient-to-b from-[#05C8E1] to-[#0082BE] px-[0.6em] text-center text-balance text-white',
        className,
      )}
    >
      {rich ? (
        // The wrapper carries the prefix size as its own font-size, so the
        // `em`-based rules in `.promo-badge-rich` scale with the breakpoint
        // exactly as the tiers do.
        // Links keep the badge's white: the default primary link colour
        // would vanish against the blue gradient.
        <div
          className={classNames(
            'promo-badge-rich [&_a]:text-inherit',
            sizes.prefix,
          )}
        >
          <RichText nodes={rich} />
        </div>
      ) : (
        <>
          {prefix && (
            <Text as="span" field="badgePrefix" className={sizes.prefix}>
              {prefix}
            </Text>
          )}
          {price && (
            <Text as="span" field="badgePrice" className={sizes.price}>
              {price}
            </Text>
          )}
          {unit && (
            <Text as="span" field="badgeUnit" className={sizes.unit}>
              {unit}
            </Text>
          )}
        </>
      )}
    </div>
  );
}
